// src/firebase/wordBank.js
// The bundled fallback WORD_BANK is large, so it is only ever loaded via dynamic
// import() to keep it out of the main chunk.
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from './index';

let fallbackBank = null;
const loadFallbackBank = async () => {
  if (!fallbackBank) {
    fallbackBank = (await import('../data/wordBank')).WORD_BANK;
  }
  return fallbackBank;
};

const ensureFallbackBank = async () => {
  try {
    await loadFallbackBank();
  } catch (e) {
    console.warn('Fallback word bank failed to load:', e);
  }
};

export const uploadWordBank = async () => {
  const { WORD_BANK } = await import('../data/wordBank');
  const englishWords = WORD_BANK.en;

  try {
    // This tells Firestore: "Go to wordBank/en and put this list there"
    await setDoc(doc(db, 'wordBank', 'en'), {
      words: englishWords
    });
    console.log("Success! All words are now in Firestore.");
  } catch (e) {
    console.error("Error uploading:", e);
  }
};

// In-memory cache for word banks with TTL (5 minutes = 300000 ms)
const WORD_BANK_CACHE_TTL = 5 * 60 * 1000;
let wordBankCache = { en: null, loadedAt: 0 };

/**
 * Populate the word cache (Firestore, or the bundled fallback). Await this
 * before calling getWordChoices(), which is synchronous.
 */
export const loadWordBank = async (lang = 'en') => {
  const now = Date.now();
  // Return cached words if still fresh
  if (wordBankCache[lang] && now - wordBankCache.loadedAt < WORD_BANK_CACHE_TTL) {
    if (!wordBankCache.en?.length) await ensureFallbackBank();
    return;
  }

  try {
    const snap = await getDoc(doc(db, 'wordBank', lang));
    if (snap.exists()) {
      wordBankCache[lang] = snap.data().words || [];
      wordBankCache.loadedAt = now;
    }
  } catch (e) {
    console.warn('Word bank load failed, using fallback:', e);
    try {
      const bank = await loadFallbackBank();
      wordBankCache[lang] = bank[lang] || bank.en;
      wordBankCache.loadedAt = now;
    } catch (importErr) {
      console.warn('Fallback word bank failed to load:', importErr);
    }
  }

  // getWordChoices falls back to the bundled list when Firestore gave us nothing
  if (!wordBankCache.en?.length) await ensureFallbackBank();
};

export const getWordChoices = (count = 3, usedWords = []) => {
  const pool = wordBankCache.en?.length ? wordBankCache.en : (fallbackBank?.en || []);
  const available = pool.filter(w => !usedWords.includes(w));
  const source = available.length >= count ? available : pool;
  return [...source].sort(() => Math.random() - 0.5).slice(0, count);
};

// ─── Hints ─────────────────────────────────────────────────────────────────

export const generateHint = (word) => {
  return word.split(' ').map(w =>
    w.split('').map((ch, i) => (i === 0 ? ch : '_')).join('')
  ).join(' ');
};

export const revealHintCharacter = (word, hint) => {
  const wordArr = word.split('');
  const hintArr = hint.split('');
  const hidden = hintArr.map((c, i) => c === '_' ? i : null).filter(i => i !== null);
  if (!hidden.length) return hint;
  const revealIdx = hidden[Math.floor(Math.random() * hidden.length)];
  const newHint = hintArr.map((c, i) => i === revealIdx ? wordArr[i] : c);
  return newHint.join('');
};
